import * as React from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import { TextareaAutosize } from "@mui/base";
import EmailInputField from "../../../../components/EmailInputField/EmailInputField";
import { normalizeEmails, isValidEmailList } from "../../../../utils/emailValidation";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Grid from "@mui/material/Grid";
import { useDispatch } from "react-redux";
import {
  saveBillToDocDeliveryReq,
  getBillToDocDeliveryReq,
  deleteEmailDocRowReq,
  deleteSftpDocRowReq,
  deleteAllDocDeliveryReq,
  getFileNameFormatOrderFields,
  getBillToFileNameFormatConfig,
  listFileNameFormatConfigs,
  createFileNameFormatConfig,
  updateFileNameFormatConfig,
  deleteFileNameFormatConfigReq,
  lookupFileNameFormatConfig,
} from "../../../../store/actions/maintenanceBillToActions";
import { getDocumentTypeData } from "../../../../store/actions/documentAction";

// ─── App color ────────────────────────────────────────────────────────────────
const APP_BLUE = "rgb(11, 67, 135)";

// ─── Static constants ─────────────────────────────────────────────────────────
const DELIVERY_METHODS = [
  { code: "EMAIL", name: "Email" },
  { code: "SFTP", name: "SFTP" },
  { code: "FTP", name: "FTP" },
];

const FILE_FORMATS = [
  { value: "PDF", label: "PDF" },
  { value: "TIF", label: "TIF" },
];

const emptyDocRow = (id) => ({
  id,
  _ruleId: null,
  _deliveryId: null,
  docType: "",
  fileFormat: "",
  fileNameFormat: "",
  emailAddress: "",
  emailSubjectFormat: "",
  emailBodyFormat: "",
  incomingDirectory: "",
});

const emptyConnection = () => ({
  host: "",
  port: "",
  username: "",
  password: "",
});

const getFileExtension = (fileFormat) => {
  if (!fileFormat) return "";
  const f = String(fileFormat).toLowerCase();
  if (f === "pdf") return ".pdf";
  if (f === "tif") return ".tif";
  return "";
};

const stripKnownExtension = (fileNameFormat) => {
  const val = String(fileNameFormat || "").trim();
  return val.replace(/\.(pdf|tif)$/i, "");
};

const buildFileNameWithExtension = (baseName, fileFormat) => {
  const base = stripKnownExtension(baseName);
  const ext = getFileExtension(fileFormat);
  if (!base) return "";
  return `${base}${ext}`;
};

const DEFAULT_FILE_NAME_BASE = "EGSY_{orderReferenceNumber}_{timestamp}";

// ─── Shared input styles ──────────────────────────────────────────────────────
const enabledInputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#aaa" },
    "&:hover fieldset": { borderColor: APP_BLUE },
    "&.Mui-focused fieldset": { borderColor: APP_BLUE },
  },
  "& .MuiInputBase-input": { color: "#000", WebkitTextFillColor: "#000" },
};

const disabledInputSx = {
  "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
  "& .MuiInputBase-input": { color: "#000", WebkitTextFillColor: "#000" },
};

const enabledSelectSx = {
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#aaa" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: APP_BLUE },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: APP_BLUE },
  "& .MuiSelect-select": { color: "#000" },
};

const disabledSelectSx = {
  backgroundColor: "#fff",
  "& .MuiSelect-select": { color: "#000", WebkitTextFillColor: "#000" },
};

// ─── Shared TableCell style ───────────────────────────────────────────────────
const thSx = {
  backgroundColor: APP_BLUE,
  color: "#fff",
  fontSize: "0.75rem",
  fontWeight: 600,
  padding: "0px 8px",
  borderRight: "1px solid rgba(255,255,255,0.15)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const tdSx = {
  padding: "4px 6px",
  borderBottom: "1px solid #e0e0e0",
  verticalAlign: "middle",
};

const subThSx = {
  backgroundColor: "#dce6f5",
  color: "#1a3a6e",
  fontSize: "0.75rem",
  fontWeight: 600,
  padding: "0px 8px",
  borderRight: "1px solid #b0c4de",
  borderBottom: "1px solid #b0c4de",
  whiteSpace: "nowrap",
};

// ─── Textarea styles ──────────────────────────────────────────────────────────
const enabledTextareaSx = {
  width: "100%",
  boxSizing: "border-box",
  display: "block",
  fontSize: "0.80rem",
  padding: "4px 8px",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  backgroundColor: "#fff",
  border: "1px solid #aaa",
  borderRadius: "4px",
  color: "#000",
  outline: "none",
  resize: "vertical",
};

const disabledTextareaSx = {
  width: "100%",
  boxSizing: "border-box",
  display: "block",
  fontSize: "0.80rem",
  padding: "4px 8px",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  backgroundColor: "#f5f5f5",
  border: "1px solid #ddd",
  borderRadius: "4px",
  color: "#000",
  cursor: "not-allowed",
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const cellSx = { fontSize: "0.80rem" };
const ipSx = { style: { fontSize: "0.80rem", padding: "4px 8px" } };

function DocTypeSelect({
  value,
  onChange,
  disabled,
  documentTypeOptions,
  selectedDocTypes,
}) {
  return (
    <Select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      displayEmpty
      fullWidth
      sx={
        disabled
          ? { fontSize: "0.80rem", ...disabledSelectSx }
          : { fontSize: "0.80rem", ...enabledSelectSx }
      }
      inputProps={ipSx}
    >
      <MenuItem value="" sx={cellSx} disabled>
        <em>Select</em>
      </MenuItem>
      {documentTypeOptions.map((opt) => (
        <MenuItem
          key={opt.value}
          value={opt.value}
          sx={cellSx}
          disabled={selectedDocTypes.includes(opt.value) && opt.value !== value}
        >
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );
}

function FileFormatSelect({ value, onChange, disabled }) {
  return (
    <Select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      displayEmpty
      fullWidth
      sx={
        disabled
          ? { fontSize: "0.80rem", ...disabledSelectSx }
          : { fontSize: "0.80rem", ...enabledSelectSx }
      }
      inputProps={ipSx}
    >
      <MenuItem value="" sx={cellSx} disabled>
        <em>Select</em>
      </MenuItem>
      {FILE_FORMATS.map((f) => (
        <MenuItem key={f.value} value={f.value} sx={cellSx}>
          {f.label}
        </MenuItem>
      ))}
    </Select>
  );
}

function CellTextField({ value, onChange, disabled, placeholder, type, multiline, minRows, maxRows }) {
  return (
    <TextField
      size="small"
      fullWidth
      type={type || "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder || ""}
      multiline={multiline}
      minRows={minRows}
      maxRows={maxRows}
      inputProps={ipSx}
      sx={disabled ? disabledInputSx : enabledInputSx}
    />
  );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
function ConfirmDialog({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel = "Confirm",
  confirmColor = "error",
}) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "0.90rem" }}>{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          variant="outlined"
          size="small"
          sx={{ fontSize: "0.85rem", color: APP_BLUE, borderColor: APP_BLUE }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          size="small"
          color={confirmColor}
          sx={{
            fontSize: "0.85rem",
            background: APP_BLUE,
            "&:hover": { backgroundColor: "rgb(8, 50, 105)" },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Email Body Popup ─────────────────────────────────────────────────────────
function EmailBodyPopup({ open, value, onClose, onSave }) {
  const [localValue, setLocalValue] = React.useState(value || "");
  React.useEffect(() => {
    setLocalValue(value || "");
  }, [value, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600, color: APP_BLUE }}>
        Email Body
      </DialogTitle>
      <DialogContent>
        <TextField
          multiline
          rows={10}
          fullWidth
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          placeholder="Enter email body content..."
          inputProps={{ style: { fontSize: "0.85rem" } }}
          sx={{ mt: 1, ...enabledInputSx }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ fontSize: "0.85rem", color: APP_BLUE, borderColor: APP_BLUE }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            onSave(localValue);
            onClose();
          }}
          variant="contained"
          size="small"
          sx={{
            fontSize: "0.85rem",
            backgroundColor: APP_BLUE,
            "&:hover": { backgroundColor: "rgb(8,50,105)" },
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Helper: extract token names already used in a fileNameBase string ────────
// Matches patterns like {tokenName} or {tokenName:left:10} etc.
const extractUsedTokens = (fileNameBase) => {
  const matches = (fileNameBase || "").match(/\{([^}:]+)/g) || [];
  return matches.map((m) => m.replace("{", ""));
};

// ─── Helper: validate file name base for dangerous characters ─────────────────
// Allows: letters, digits, _, -, ., spaces, and token syntax chars { } :
// Blocks: / \ : * ? " < > | which are illegal in filenames on Windows/SFTP servers.
// Note: : is allowed only inside token syntax e.g. {token:left:10}, so we strip
// all valid token blocks first before checking for a bare colon.
const DANGEROUS_CHARS_REGEX = /[/\\*?"<>|]/;
const validateFileNameBase = (fileNameBase) => {
  const val = fileNameBase || "";
  // Strip token blocks like {orderReferenceNumber:left:10} before bare-colon check
  const withoutTokens = val.replace(/\{[^}]*\}/g, "");
  if (DANGEROUS_CHARS_REGEX.test(val)) {
    const found = val.match(/[/\\*?"<>|]/g);
    const unique = [...new Set(found)].map((c) => `"${c}"`).join(", ");
    return `File name format contains invalid character(s): ${unique}. These are not allowed in file names.`;
  }
  // Check for bare colon outside of token blocks
  if (withoutTokens.includes(":")) {
    return `File name format contains an invalid character: ":". Colons are only allowed inside token syntax e.g. {token:left:10}.`;
  }
  return null; // valid
};

function FileNameFormatModal({
  open,
  onClose,
  methodCode,
  billToCode,
  orderFieldTokens,
  documentTypeOptions,
  dispatch,
}) {
  const isSftpFtp = methodCode === "SFTP" || methodCode === "FTP";

  // ── state ──────────────────────────────────────────────────────────────────
  const [configs, setConfigs] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null); // null | 'new' | number
  const [editForm, setEditForm] = React.useState(emptyEditForm());
  const [savingId, setSavingId] = React.useState(null);
  const [deletingId, setDeletingId] = React.useState(null);
  const [pendingDeleteId, setPendingDeleteId] = React.useState(null);
  const [snack, setSnack] = React.useState({ open: false, msg: "", severity: "info" });

  function emptyEditForm() {
    return { docTypeCode: "", fileFormat: "", fileNameBase: DEFAULT_FILE_NAME_BASE, incomingDirectory: "" };
  }

  // ── load configs when opening ──────────────────────────────────────────────
  const loadConfigs = React.useCallback(async () => {
    if (!methodCode) return;
    setIsLoading(true);
    const data = await dispatch(listFileNameFormatConfigs(methodCode, billToCode));
    setConfigs(Array.isArray(data) ? data : []);
    setIsLoading(false);
  }, [methodCode, billToCode, dispatch]);

  React.useEffect(() => {
    if (open) {
      loadConfigs();
      setEditingId(null);
      setEditForm(emptyEditForm());
    }
  }, [open, loadConfigs]);

  // ── Bug 3 fix: token insertion — prevent duplicate tokens ─────────────────
  const insertToken = (token) => {
    const usedTokens = extractUsedTokens(editForm.fileNameBase);
    // Extract base token name (without formatting syntax like :left:10)
    const baseTokenName = token.replace(/^\{/, "").replace(/\}$/, "").split(":")[0];
    if (usedTokens.includes(baseTokenName)) {
      setSnack({ open: true, msg: `Token "${baseTokenName}" is already used in this file name format.`, severity: "warning" });
      return;
    }
    setEditForm((prev) => ({ ...prev, fileNameBase: `${prev.fileNameBase || ""}${token}` }));
  };

  // Duplicate = same docType for the same billTo
  const usedDocTypeCodes = React.useMemo(() => {
    const otherConfigs = configs.filter((c) => c.id !== editingId);
    return otherConfigs.map((c) => String(c.document_type_code));
  }, [configs, editingId]);

  // ── start new / edit ──────────────────────────────────────────────────────
  const startNew = () => {
    setEditingId("new");
    setEditForm(emptyEditForm());
  };

  const startEdit = (cfg) => {
    setEditingId(cfg.id);
    setEditForm({
      docTypeCode: String(cfg.document_type_code),
      fileFormat: cfg.file_format || "",
      fileNameBase: stripKnownExtension(cfg.file_name_format || "") || DEFAULT_FILE_NAME_BASE,
      incomingDirectory: cfg.incoming_directory || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(emptyEditForm());
  };

  // ── save (create or update) ────────────────────────────────────────────────
  const saveRow = async () => {
    if (!editForm.docTypeCode) {
      setSnack({ open: true, msg: "Please select a document type.", severity: "warning" });
      return;
    }

    // Bug 7 fix: file format is required
    if (!editForm.fileFormat) {
      setSnack({ open: true, msg: "Please select a file format.", severity: "warning" });
      return;
    }

    // Bug 7 fix: incoming directory is required for SFTP/FTP
    if (isSftpFtp && !editForm.incomingDirectory.trim()) {
      setSnack({ open: true, msg: "Incoming directory is required for SFTP/FTP.", severity: "warning" });
      return;
    }

    // Bug 7 fix: file name base is required
    if (!editForm.fileNameBase.trim()) {
      setSnack({ open: true, msg: "File name format is required.", severity: "warning" });
      return;
    }

    // Clarification 4: block dangerous characters in file name format
    const fileNameError = validateFileNameBase(editForm.fileNameBase);
    if (fileNameError) {
      setSnack({ open: true, msg: fileNameError, severity: "error" });
      return;
    }

    const isDuplicate = usedDocTypeCodes.includes(editForm.docTypeCode);
    if (isDuplicate) {
      setSnack({ open: true, msg: `Document type already configured for bill-to ${billToCode}.`, severity: "error" });
      return;
    }

    const fileNameFormat = buildFileNameWithExtension(editForm.fileNameBase, editForm.fileFormat);
    const payload = {
      deliveryMethodCode: methodCode,
      docTypeCode: Number(editForm.docTypeCode),
      billToCode,
      fileFormat: editForm.fileFormat || undefined,
      fileNameFormat: fileNameFormat || undefined,
      incomingDirectory: editForm.incomingDirectory || undefined,
    };

    setSavingId(editingId);
    let res;
    if (editingId === "new") {
      res = await dispatch(createFileNameFormatConfig(payload));
    } else {
      res = await dispatch(updateFileNameFormatConfig(editingId, payload));
    }
    setSavingId(null);

    if (res?.success) {
      setSnack({ open: true, msg: editingId === "new" ? "Config created." : "Config updated.", severity: "success" });
      cancelEdit();
      loadConfigs();
    } else {
      setSnack({ open: true, msg: res?.error || "Save failed.", severity: "error" });
    }
  };

  // ── delete ─────────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    setDeletingId(pendingDeleteId);
    const res = await dispatch(deleteFileNameFormatConfigReq(pendingDeleteId));
    setDeletingId(null);
    setPendingDeleteId(null);
    if (res?.success) {
      setSnack({ open: true, msg: "Config deleted.", severity: "success" });
      loadConfigs();
    } else {
      setSnack({ open: true, msg: res?.error || "Delete failed.", severity: "error" });
    }
  };

  const currentTokens = orderFieldTokens || [];
  const isEditing = editingId !== null;

  // Bug 3: compute which token names are already used in current fileNameBase
  const alreadyUsedTokenNames = React.useMemo(
    () => extractUsedTokens(editForm.fileNameBase),
    [editForm.fileNameBase]
  );

  const modalFieldInputSx = {
    ...enabledInputSx,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      height: "32px",
      "& fieldset": { borderColor: "#aaa" },
      "&:hover fieldset": { borderColor: APP_BLUE },
      "&.Mui-focused fieldset": { borderColor: APP_BLUE },
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600, color: APP_BLUE }}>
        File Name Format Config — {methodCode || "Delivery Method"}
      </DialogTitle>
      <DialogContent sx={{ py: 1.5, px: 2 }}>

        {/* ── Token help + buttons ── */}
        <Box sx={{ bgcolor: "#f0f4ff", border: "1px solid #c8d8f0", borderRadius: 0.75, p: 0.75, mb: 1.25 }}>
          <Typography sx={{ fontSize: "0.75rem", color: "#333", mb: 0.3, lineHeight: 1.3 }}>
            <strong>Tokens:</strong> click to insert (each token can only be used once). Syntax: {"{"}token{"}"} or {"{"}token:left:N{"}"}. Example: {"{"}orderReferenceNumber:left:15{"}"} {"{"}randomNumber:right:3{"}"}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.4 }}>
            {currentTokens.map((t) => {
              // Bug 3: extract base token name to check if already used
              const baseTokenName = t.token.replace(/^\{/, "").replace(/\}$/, "").split(":")[0];
              const alreadyUsed = isEditing && alreadyUsedTokenNames.includes(baseTokenName);
              return (
                <Button
                  key={t.token}
                  size="small"
                  variant="outlined"
                  disabled={!isEditing || alreadyUsed}
                  onClick={() => insertToken(t.token)}
                  title={alreadyUsed ? `"${baseTokenName}" already used` : `${t.label} — e.g. ${t.example}`}
                  sx={{
                    fontSize: "0.65rem",
                    textTransform: "none",
                    color: alreadyUsed ? "#aaa" : APP_BLUE,
                    borderColor: alreadyUsed ? "#ddd" : APP_BLUE,
                    py: 0,
                    px: 0.75,
                    minHeight: 22,
                    height: 22,
                    textDecoration: alreadyUsed ? "line-through" : "none",
                  }}
                >
                  {t.token}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* ── Edit / New row form ── */}
        {isEditing && (
          <Box sx={{ border: `1px solid ${APP_BLUE}`, borderRadius: 0.75, p: 1.25, mb: 1.5, bgcolor: "#f8fbff" }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: APP_BLUE, mb: 1 }}>
              {editingId === "new" ? "New Config" : "Edit Config"}
            </Typography>
            
            {/* Row 1 layout by method */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isSftpFtp ? "20% 13% 67%" : "20% 13% 67%",
                gap: 0.75,
                alignItems: "flex-end",
                mb: 1,
              }}
            >
              {/* Doc Type */}
              <Box>
                <Typography sx={{ fontSize: "0.75rem", color: "#555", mb: 0.3, fontWeight: 500 }}>Document Type *</Typography>
                <Select
                  size="small"
                  value={editForm.docTypeCode}
                  onChange={(e) => setEditForm((p) => ({ ...p, docTypeCode: e.target.value }))}
                  displayEmpty
                  disabled={editingId !== "new"}
                  fullWidth
                  sx={{ fontSize: "0.85rem", ...enabledSelectSx }}
                  inputProps={ipSx}
                >
                  <MenuItem value="" sx={cellSx}><em>Select</em></MenuItem>
                  {documentTypeOptions.map((opt) => (
                    <MenuItem
                      key={opt.value}
                      value={opt.value}
                      sx={cellSx}
                      disabled={usedDocTypeCodes.includes(opt.value)}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              
              {/* File Format */}
              <Box>
                <Typography sx={{ fontSize: "0.75rem", color: "#555", mb: 0.3, fontWeight: 500 }}>File Format *</Typography>
                <FileFormatSelect
                  value={editForm.fileFormat}
                  onChange={(val) => setEditForm((p) => ({ ...p, fileFormat: val }))}
                  disabled={false}
                />
              </Box>
              
              {/* Incoming Directory (FTP/SFTP only) / File Name (EMAIL) */}
              {isSftpFtp ? (
                <Box>
                  <Typography sx={{ fontSize: "0.75rem", color: "#555", mb: 0.3, fontWeight: 500 }}>Incoming Dir *</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={editForm.incomingDirectory}
                    onChange={(e) => setEditForm((p) => ({ ...p, incomingDirectory: e.target.value }))}
                    inputProps={ipSx}
                    sx={modalFieldInputSx}
                    placeholder="\\pod\\"
                  />
                </Box>
              ) : (
                <Box>
                  <Typography sx={{ fontSize: "0.75rem", color: "#555", mb: 0.3, fontWeight: 500 }}>File Name Format *</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={editForm.fileNameBase}
                    onChange={(e) => setEditForm((p) => ({ ...p, fileNameBase: e.target.value }))}
                    inputProps={ipSx}
                    sx={modalFieldInputSx}
                    placeholder={DEFAULT_FILE_NAME_BASE}
                  />
                </Box>
              )}
              
            </Box>

            {/* Row 2: File Name Format (FTP/SFTP only) */}
            {isSftpFtp && (
              <Box sx={{ mb: 1.25 }}>
                <Typography sx={{ fontSize: "0.8rem", color: "#333", mb: 0.4, fontWeight: 600 }}>File Name Format *</Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={editForm.fileNameBase}
                  onChange={(e) => setEditForm((p) => ({ ...p, fileNameBase: e.target.value }))}
                  inputProps={ipSx}
                  sx={modalFieldInputSx}
                  placeholder={DEFAULT_FILE_NAME_BASE}
                />
              </Box>
            )}
            
            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 0.75, pt: 0.25 }}>
              <Button
                size="small"
                variant="contained"
                onClick={saveRow}
                disabled={savingId !== null}
                sx={{ fontSize: "0.75rem", py: 0.4, px: 1.5, backgroundColor: APP_BLUE, "&:hover": { backgroundColor: "rgb(8,50,105)" } }}
              >
                {savingId !== null ? "Saving…" : "Save"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={cancelEdit}
                disabled={savingId !== null}
                sx={{ fontSize: "0.75rem", py: 0.4, px: 1.5, color: APP_BLUE, borderColor: APP_BLUE }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {/* ── Configs table ── */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5, mt: 0.75 }}>
          <Typography sx={{ fontSize: "0.75rem", color: "#666" }}>
            {isLoading ? "Loading…" : `${configs.length} config(s)`}
          </Typography>
          {!isEditing && (
            <Button
              size="small"
              variant="outlined"
              onClick={startNew}
              sx={{ fontSize: "0.7rem", textTransform: "none", py: 0.3, px: 1, color: APP_BLUE, borderColor: APP_BLUE }}
            >
              + Add
            </Button>
          )}
        </Box>

        <TableContainer sx={{ border: "1px solid #ccc", borderRadius: 1 }}>
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "13%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: isSftpFtp ? "32%" : "60%" }} />
              {isSftpFtp && <col style={{ width: "22%" }} />}
              <col style={{ width: "13%" }} />
              <col style={{ width: "10%" }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>DOC Type</TableCell>
                <TableCell sx={thSx}>Format</TableCell>
                <TableCell sx={thSx}>File Name Format</TableCell>
                {isSftpFtp && <TableCell sx={thSx}>Incoming Directory</TableCell>}
                <TableCell sx={thSx}>Bill-To</TableCell>
                <TableCell sx={thSx}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={isSftpFtp ? 6 : 5} sx={{ ...tdSx, textAlign: "center", color: "#888", fontSize: "0.75rem" }}>
                    No configurations yet. Click "+Add" to create one.
                  </TableCell>
                </TableRow>
              )}
              {configs.map((cfg) => (
                <TableRow key={cfg.id} sx={editingId === cfg.id ? { bgcolor: "#eef4ff" } : {}}>
                  <TableCell sx={{ ...tdSx, fontSize: "0.75rem" }}>{cfg.doc_type_label}</TableCell>
                  <TableCell sx={{ ...tdSx, fontSize: "0.75rem" }}>{cfg.file_format || "—"}</TableCell>
                  <TableCell sx={{ ...tdSx, wordBreak: "break-all", fontSize: "0.75rem" }}>{cfg.file_name_format || "—"}</TableCell>
                  {isSftpFtp && <TableCell sx={{ ...tdSx, fontSize: "0.75rem" }}>{cfg.incoming_directory || "—"}</TableCell>}
                  <TableCell sx={{ ...tdSx, fontSize: "0.75rem" }}>{cfg.bill_to_code || <em style={{ color: "#888" }}>Global</em>}</TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                    <IconButton
                      size="small"
                      onClick={() => startEdit(cfg)}
                      disabled={isEditing || deletingId === cfg.id}
                      title="Edit"
                    >
                      <EditIcon sx={{ fontSize: "0.95rem", color: APP_BLUE }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setPendingDeleteId(cfg.id)}
                      disabled={isEditing || deletingId === cfg.id}
                      title="Delete"
                    >
                      <DeleteIcon sx={{ fontSize: "0.95rem", color: "#c62828" }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ py: 1, px: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="small"
          sx={{ fontSize: "0.75rem", py: 0.4, px: 2, backgroundColor: APP_BLUE, "&:hover": { backgroundColor: "rgb(8,50,105)" } }}
        >
          Done
        </Button>
      </DialogActions>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!pendingDeleteId}
        title="Delete Configuration"
        message="Are you sure you want to delete this file name format configuration? This cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />

      {/* Snack feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function InvoiceDocDeliveryGrid({ billToCode, onDirtyChange }) {
  const dispatch = useDispatch();

  const [isEditMode, setIsEditMode] = React.useState(true);
  const [selectedMethodCode, setSelectedMethodCode] = React.useState("");
  const [connection, setConnection] = React.useState(emptyConnection());
  const [docRows, setDocRows] = React.useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = React.useState([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [snackContent, setSnackContent] = React.useState({
    severity: "info",
    message: "",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [pendingDeleteId, setPendingDeleteId] = React.useState(null);
  const [methodChangeDialogOpen, setMethodChangeDialogOpen] =
    React.useState(false);
  const [pendingMethodCode, setPendingMethodCode] = React.useState(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [persistedDeliveryId, setPersistedDeliveryId] = React.useState(null);
  const [snapshot, setSnapshot] = React.useState(null);
  const [isNewConfig, setIsNewConfig] = React.useState(true);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [fileNameModalOpen, setFileNameModalOpen] = React.useState(false);
  const [orderFieldTokens, setOrderFieldTokens] = React.useState([]);
  const isAddingNew = React.useRef(false);

  React.useEffect(() => {
    dispatch(getDocumentTypeData()).then((res) => {
      const list = Array.isArray(res) ? res : res?.data || [];
      setDocumentTypeOptions(
        list.map((d) => ({
          value: String(d.document_type_code),
          label: d.abbrevation,
        })),
      );
    });

    dispatch(getFileNameFormatOrderFields()).then((rows) => {
      setOrderFieldTokens(Array.isArray(rows) ? rows : []);
    });
  }, [dispatch]);

  React.useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty]);

  const markDirty = () => {
    if (!isDirty) setIsDirty(true);
  };

  React.useEffect(() => {
    if (!billToCode) return;

    setIsLoading(true);
    dispatch(getBillToDocDeliveryReq(billToCode))
      .then((res) => {
        if (!res?.success || !res?.data?.docDeliveries?.length) {
          setIsNewConfig(true);
          setIsEditMode(true);
          setDocRows([{ ...emptyDocRow(Date.now()) }]);
          isAddingNew.current = true;
          return;
        }
        setIsNewConfig(false);

        const delivery = res.data.docDeliveries[0];
        const deliveryId = delivery?.deliveryId ?? delivery?.delivery_id ?? null;
        const { deliveryMethodCode, config } = delivery;

        setPersistedDeliveryId(deliveryId ?? null);
        setSelectedMethodCode(deliveryMethodCode || "");

        if (deliveryMethodCode === "EMAIL") {
          // EMAIL: config is an array of document rows
          const rows = Array.isArray(config) ? config : [];
          setDocRows(
            rows.map((d) => ({
              id: d.ruleId ?? Date.now() + Math.random(),
              _ruleId: d.ruleId ?? null,
              _deliveryId: null,
              docType: String(d.documentCode || ""),
              fileFormat: d.fileFormat || "",
              fileNameFormat: d.fileNameFormat || "",
              emailAddress: d.emailAddress || "",
              emailSubjectFormat: d.emailSubjectFormat || "",
              emailBodyFormat: d.emailBodyFormat || "",
              incomingDirectory: "",
            })),
          );
        }

        if (deliveryMethodCode === "SFTP" || deliveryMethodCode === "FTP") {
          // SFTP/FTP: config is an object with connection + documents array
          setConnection({
            host: config.host || "",
            port: String(config.port || ""),
            username: config.username || "",
            password: config.password || "",
          });

          const docs = Array.isArray(config.documents) ? config.documents : [];
          setDocRows(
            docs.map((d) => ({
              id: d.ruleId ?? Date.now() + Math.random(),
              _ruleId: d.ruleId ?? null,
              _deliveryId: deliveryId,
              docType: String(d.documentCode || ""),
              fileFormat: d.fileFormat || "",
              fileNameFormat: d.fileNameFormat || "",
              incomingDirectory: d.incomingDirectory || "",
              emailAddress: "",
              emailSubjectFormat: "",
              emailBodyFormat: "",
            })),
          );
        }
      })
      .finally(() => setIsLoading(false));
  }, [billToCode]);

  // Bug 6: Removed the celtic/mode auto-binding useEffect entirely.
  // File name format and incoming directory are now configured exclusively
  // via the gear icon (FileNameFormatModal).

  const isEmail = selectedMethodCode === "EMAIL";
  const isSftpFtp =
    selectedMethodCode === "SFTP" || selectedMethodCode === "FTP";

  const handleMethodChange = (code) => {
    if (!isNewConfig && selectedMethodCode && code !== selectedMethodCode) {
      setPendingMethodCode(code);
      setMethodChangeDialogOpen(true);
      return;
    }
    applyMethodChange(code);
  };

  const applyMethodChange = (code) => {
    setSelectedMethodCode(code);
    setConnection(emptyConnection());
    setDocRows([{ ...emptyDocRow(Date.now()) }]);
    setMethodChangeDialogOpen(false);
    setPendingMethodCode(null);
    if (isNewConfig) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  };

  const updateConnection = (field, value) => {
    setConnection((p) => ({ ...p, [field]: value }));
    markDirty();
  };

  const updateDocRow = (id, field, value) => {
    setDocRows((p) =>
      p.map((r) => {
        if (r.id !== id) return r;
        if (field === "fileFormat") {
          const base = stripKnownExtension(r.fileNameFormat || "");
          return {
            ...r,
            fileFormat: value,
            fileNameFormat: buildFileNameWithExtension(base, value),
          };
        }
        return { ...r, [field]: value };
      }),
    );
    markDirty();
  };

  // ── Doc type selection: call lookup API to prefill ──────────────────────────
  const handleDocTypeSelect = async (rowId, docTypeCode) => {
    // Optimistically update the doc type immediately
    setDocRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        
        // For EMAIL method: populate default templates when docType is first selected
        const defaultEmailSubject = "Documents Order {orderReferenceNumber} - {unit} - {documentName}";

const defaultEmailBody =
    'Please find attached the document(s).\n' +
    'Reference Number: {{orderReferenceNumber}}\n' +
    'Unit No: {{unit}}\n' +
    'Document: {{documentName}}\n' +
    'ESI Invoice Number: {{invoiceNumber}}\n';
        
        const populateEmailDefaults = isEmail && !row.emailSubjectFormat && !row.emailBodyFormat;
        
        return {
          ...row,
          docType: docTypeCode,
          ...(populateEmailDefaults && {
            emailSubjectFormat: defaultEmailSubject,
            emailBodyFormat: defaultEmailBody,
          }),
        };
      }),
    );
    markDirty();

    if (!docTypeCode || !selectedMethodCode) return;

    // Lookup best match from DB (customer-specific first, then global)
    const template = await dispatch(
      lookupFileNameFormatConfig(selectedMethodCode, Number(docTypeCode), billToCode),
    );

    if (!template) return;

    setDocRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const nextFileFormat = template.file_format || row.fileFormat || "";
        const baseFromTemplate = stripKnownExtension(
          template.file_name_format || row.fileNameFormat || "",
        );
        return {
          ...row,
          fileFormat: nextFileFormat,
          fileNameFormat: buildFileNameWithExtension(baseFromTemplate, nextFileFormat),
          incomingDirectory:
            selectedMethodCode === "FTP" || selectedMethodCode === "SFTP"
              ? template.incoming_directory || row.incomingDirectory || ""
              : row.incomingDirectory,
        };
      }),
    );
  };

  const openFileNameModal = () => {
    if (!selectedMethodCode) {
      setSnackContent({ severity: "warning", message: "Please select a delivery method first." });
      setSnackOpen(true);
      return;
    }
    setFileNameModalOpen(true);
  };

  const addDocRow = () => {
    if (docRows.some((r) => !r.docType)) {
      setSnackContent({
        severity: "warning",
        message:
          "Please select DOC Type for existing rows before adding a new one.",
      });
      setSnackOpen(true);
      return;
    }
    setDocRows((p) => [...p, { ...emptyDocRow(Date.now()) }]);
    markDirty();
  };

  const handleDeleteClick = (id) => {
    setPendingDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const row = docRows.find((r) => r.id === pendingDeleteId);
    const deliveryIdForRow = row?._deliveryId ?? persistedDeliveryId;

    if (row?._ruleId) {
      // Persisted row — call DELETE API
      setIsDeleting(true);
      let res = null;

      if (isEmail) {
        res = await dispatch(deleteEmailDocRowReq(billToCode, row._ruleId));
      }

      if (isSftpFtp) {
        res = await dispatch(
          deleteSftpDocRowReq(billToCode, deliveryIdForRow, row._ruleId),
        );
      }

      setIsDeleting(false);

      const deleteSucceeded =
        res?.success === true ||
        res?.status === "success" ||
        res?.httpCode === 200;

      if (deleteSucceeded) {
        setDocRows((p) => p.filter((r) => r.id !== pendingDeleteId));
        setSnackContent({
          severity: "success",
          message: res?.message || "Row deleted successfully.",
        });
      } else {
        setSnackContent({
          severity: "error",
          message: res?.message || res?.error || "Failed to delete row.",
        });
      }
      setSnackOpen(true);
    } else {
      // New unsaved row — just remove locally
      setDocRows((p) => p.filter((r) => r.id !== pendingDeleteId));
      markDirty();
    }

    setDeleteDialogOpen(false);
    setPendingDeleteId(null);
  };

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    const res = await dispatch(deleteAllDocDeliveryReq(billToCode));
    setIsDeleting(false);
    setDeleteAllDialogOpen(false);

    if (res?.success) {
      setSelectedMethodCode("");
      setConnection(emptyConnection());
      setDocRows([]);
      setPersistedDeliveryId(null);
      setIsNewConfig(true);
      setIsEditMode(true);
      setDocRows([{ ...emptyDocRow(Date.now()) }]);
      isAddingNew.current = true;
      setIsDirty(false);
      setSnapshot(null);
      setSnackContent({
        severity: "success",
        message: res?.message || "All delivery config deleted.",
      });
    } else {
      setSnackContent({
        severity: "error",
        message: res?.message || "Failed to delete delivery config.",
      });
    }
    setSnackOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPendingDeleteId(null);
  };

  const selectedDocTypes = docRows.map((r) => r.docType).filter(Boolean);
  const allDocTypesUsed =
    documentTypeOptions.length > 0 &&
    documentTypeOptions.every((o) => selectedDocTypes.includes(o.value));

  const handleSave = () => {
    if (!selectedMethodCode) {
      setSnackContent({
        severity: "error",
        message: "Please select a Doc Delivery Method.",
      });
      setSnackOpen(true);
      return;
    }

    // Bug 8: Block save if no rows have a doc type selected
    const filledRows = docRows.filter((r) => r.docType);
    if (filledRows.length === 0) {
      setSnackContent({
        severity: "error",
        message: "Please add at least one document row with a DOC Type selected.",
      });
      setSnackOpen(true);
      return;
    }

    if (isEmail) {
      const inv = docRows.filter(
        (r) =>
          !r.docType || !r.emailAddress || !r.fileNameFormat,
      );
      if (inv.length > 0) {
        setSnackContent({
          severity: "error",
          message:
            "Please fill all required fields for every row: DOC Type, Email Address, and File Name Format.",
        });
        setSnackOpen(true);
        return;
      }

      const invalidEmail = docRows.filter((r) => !isValidEmailList(r.emailAddress));
      if (invalidEmail.length > 0) {
        setSnackContent({
          severity: "error",
          message: `Invalid email address list. Use comma-separated emails (example: a@x.com, b@y.com).`,
        });
        setSnackOpen(true);
        return;
      }
    }

    if (isSftpFtp) {
      const { host, port, username, password } = connection;
      if (!host || !port || !username || !password) {
        setSnackContent({
          severity: "error",
          message:
            "Please fill all connection fields: Host, PORT, User Name, and Password.",
        });
        setSnackOpen(true);
        return;
      }

      // Bug 7: validate file format and incoming directory are required for SFTP/FTP rows
      const missingFileFormat = docRows.filter((r) => r.docType && !r.fileFormat);
      if (missingFileFormat.length > 0) {
        setSnackContent({
          severity: "error",
          message: "Please configure File Format for all document rows via the gear icon.",
        });
        setSnackOpen(true);
        return;
      }

      const inv = docRows.filter(
        (r) => !r.docType || !r.incomingDirectory,
      );
      if (inv.length > 0) {
        setSnackContent({
          severity: "error",
          message:
            "Please configure all required fields for every row: DOC Type and Incoming Directory (via gear icon).",
        });
        setSnackOpen(true);
        return;
      }
    }

    let docDeliveries = [];

    if (isEmail) {
      docDeliveries = [
        {
          ...(persistedDeliveryId && { deliveryId: persistedDeliveryId }),
          deliveryMethodCode: "EMAIL",
          config: docRows.map((row) => ({
            ...(row._ruleId && { ruleId: row._ruleId }),
            documentCode: Number(row.docType),
            fileFormat: row.fileFormat || null,
            fileNameFormat: row.fileNameFormat || null,
            emailAddress: row.emailAddress || null,
            emailSubjectFormat: row.emailSubjectFormat || null,
            emailBodyFormat: row.emailBodyFormat || null,
          })),
        },
      ];
    }

    if (isSftpFtp) {
      docDeliveries = [
        {
          ...(persistedDeliveryId && { deliveryId: persistedDeliveryId }),
          deliveryMethodCode: selectedMethodCode,
          config: {
            protocol: selectedMethodCode,
            host: connection.host || null,
            port: Number(connection.port) || null,
            username: connection.username || null,
            password: connection.password || null,
            documents: docRows.map((row) => ({
              ...(row._ruleId && { ruleId: row._ruleId }),
              documentCode: Number(row.docType),
              fileFormat: row.fileFormat || null,
              fileNameFormat: row.fileNameFormat || null,
              incomingDirectory: row.incomingDirectory || null,
            })),
          },
        },
      ];
    }

    console.log("docDeliveries ====", docDeliveries);

    setIsSaving(true);
    dispatch(saveBillToDocDeliveryReq({ docDeliveries }, billToCode))
      .then((res) => {
        if (res?.success) {
          setSnackContent({
            severity: "success",
            message: res?.message || "Saved successfully.",
          });
          setIsDirty(false);
          setIsEditMode(true);
          setIsNewConfig(false);
          isAddingNew.current = false;

          dispatch(getBillToDocDeliveryReq(billToCode)).then((freshRes) => {
            if (!freshRes?.success || !freshRes?.data?.docDeliveries?.length)
              return;

            const delivery = freshRes.data.docDeliveries[0];
            const refreshedDeliveryId = delivery?.deliveryId ?? delivery?.delivery_id ?? null;
            setPersistedDeliveryId(refreshedDeliveryId);

            const isEmailRes = delivery.deliveryMethodCode === "EMAIL";
            const isSftpFtpRes =
              delivery.deliveryMethodCode === "SFTP" ||
              delivery.deliveryMethodCode === "FTP";

            if (isEmailRes) {
              const rows = Array.isArray(delivery.config)
                ? delivery.config
                : [];
              setDocRows(
                rows.map((d) => ({
                  id: d.ruleId ?? Date.now() + Math.random(),
                  _ruleId: d.ruleId ?? null,
                  _deliveryId: null,
                  docType: String(d.documentCode || ""),
                  fileFormat: d.fileFormat || "",
                  fileNameFormat: d.fileNameFormat || "",
                  emailAddress: d.emailAddress || "",
                  emailSubjectFormat: d.emailSubjectFormat || "",
                  emailBodyFormat: d.emailBodyFormat || "",
                  incomingDirectory: "",
                })),
              );
            }

            if (isSftpFtpRes) {
              const docs = Array.isArray(delivery.config?.documents)
                ? delivery.config.documents
                : [];
              setDocRows(
                docs.map((d) => ({
                  id: d.ruleId ?? Date.now() + Math.random(),
                  _ruleId: d.ruleId ?? null,
                  _deliveryId: refreshedDeliveryId,
                  docType: String(d.documentCode || ""),
                  fileFormat: d.fileFormat || "",
                  fileNameFormat: d.fileNameFormat || "",
                  incomingDirectory: d.incomingDirectory || "",
                  emailAddress: "",
                  emailSubjectFormat: "",
                  emailBodyFormat: "",
                })),
              );
            }
          });
        } else {
          setSnackContent({
            severity: "error",
            message: res?.message || "Failed to save doc delivery.",
          });
        }
      })
      .catch(() => {
        setSnackContent({
          severity: "error",
          message: "Failed to save doc delivery.",
        });
      })
      .finally(() => {
        setIsSaving(false);
        setSnackOpen(true);
      });
  };

  const handleCancelEdit = () => {
    if (isAddingNew.current) {
      setSelectedMethodCode("");
      setConnection(emptyConnection());
      setDocRows([{ ...emptyDocRow(Date.now()) }]);
      setIsNewConfig(true);
      setIsEditMode(true);
      setIsDirty(false);
      setSnapshot(null);
      return;
    }

    if (snapshot) {
      setSelectedMethodCode(snapshot.selectedMethodCode);
      setConnection(snapshot.connection);
      setDocRows(snapshot.docRows);
    }
    setIsEditMode(true);
    setIsDirty(false);
    setSnapshot(null);
  };

  // ── EMAIL table — using MUI Table with fixed column widths ────────────────
  // Total = 100%: DOCType=18% | EmailAddr=20% | FileNameFmt=25% | SubjectFmt=15% | BodyFmt=18% | Del=4%
  const renderEmailTable = () => (
    <TableContainer
      sx={{
        border: "1px solid #ccc",
        borderRadius: 1,
        mt: 1,
        overflowX: "hidden",
      }}
    >
      <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
        <colgroup>
          <col style={{ width: "18%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "25%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "4%" }} />
        </colgroup>
        <TableHead>
          <TableRow>
            {[
              "DOC Type",
              "Email Address",
              "File Name Format",
              "Subject Format",
              "Body Format",
              "",
            ].map((h, i) => (
              <TableCell key={i} sx={thSx}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {docRows.map((row) => (
            <TableRow
              key={row.id}
              sx={{ "&:hover": { backgroundColor: "#fafafa" } }}
            >
              <TableCell sx={tdSx}>
                <DocTypeSelect
                  value={row.docType}
                  onChange={(v) => handleDocTypeSelect(row.id, v)}
                  disabled={!isEditMode && !isNewConfig}
                  documentTypeOptions={documentTypeOptions}
                  selectedDocTypes={selectedDocTypes}
                />
              </TableCell>
              <TableCell sx={tdSx}>
                <EmailInputField
                  value={normalizeEmails(row.emailAddress)}
                  onChange={(emails) => updateDocRow(row.id, "emailAddress", emails.join(", "))}
                  disabled={!isEditMode && !isNewConfig}
                  multiple={true}
                  label=""
                  placeholder="email1@x.com, email2@y.com"
                  size="small"
                  fullWidth={true}
                />
              </TableCell>
              <TableCell sx={{ ...tdSx, maxWidth: "100%", overflow: "hidden" }} title={row.fileNameFormat || ""}>
                <CellTextField
                  value={row.fileNameFormat}
                  onChange={(v) => updateDocRow(row.id, "fileNameFormat", v)}
                  disabled
                  placeholder="Configure via gear icon"
                  sx={{ fontSize: "0.75rem" }}
                />
              </TableCell>
              <TableCell sx={tdSx}>
                <TextareaAutosize
                  minRows={1}
                  maxRows={3}
                  value={row.emailSubjectFormat}
                  onChange={(e) => updateDocRow(row.id, "emailSubjectFormat", e.target.value)}
                  disabled={!isEditMode && !isNewConfig}
                  placeholder="Subject format"
                  style={!isEditMode && !isNewConfig ? disabledTextareaSx : enabledTextareaSx}
                />
              </TableCell>
              <TableCell sx={tdSx}>
                <TextareaAutosize
                  minRows={1}
                  maxRows={4}
                  value={row.emailBodyFormat}
                  onChange={(e) => updateDocRow(row.id, "emailBodyFormat", e.target.value)}
                  disabled={!isEditMode && !isNewConfig}
                  placeholder="Body format"
                  style={!isEditMode && !isNewConfig ? disabledTextareaSx : enabledTextareaSx}
                />
              </TableCell>
              <TableCell
                sx={{ ...tdSx, textAlign: "center", padding: "2px 0" }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(row.id)}
                  sx={{ p: 0.25 }}
                >
                  <DeleteIcon
                    sx={{
                      fontSize: "0.9rem",
                      color: "#d32f2f",
                    }}
                  />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ── SFTP/FTP table — two stacked fixed tables ─────────────────────────────
  // Connection: Host=38% | PORT=12% | UserName=25% | Password=25%
  // Doc rows:   DOCType=20% | FileNameFmt=40% | IncomingDir=35% | Del=5%
  const renderSftpTable = () => (
    <Box
      sx={{
        border: "1px solid #ccc",
        borderRadius: 1,
        mt: 1,
        overflow: "hidden",
      }}
    >
      {/* Connection table */}
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ tableLayout: "fixed", minWidth: 600 }}>
          <colgroup>
            <col style={{ width: "38%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "25%" }} />
            <col style={{ width: "25%" }} />
          </colgroup>
          <TableHead>
            <TableRow>
              {["Host", "PORT", "User Name", "Password"].map((h, i) => (
                <TableCell key={i} sx={thSx}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ backgroundColor: "#fafcff" }}>
              <TableCell sx={tdSx}>
                <CellTextField
                  value={connection.host}
                  onChange={(v) => updateConnection("host", v)}
                  disabled={!isEditMode && !isNewConfig}
                  placeholder="Enter Domain"
                />
              </TableCell>
              <TableCell sx={tdSx}>
                <CellTextField
                  value={connection.port}
                  onChange={(v) => {
                    if (v === "" || /^\d+$/.test(v))
                      updateConnection("port", v);
                  }}
                  disabled={!isEditMode && !isNewConfig}
                  placeholder="Enter Port"
                />
              </TableCell>
              <TableCell sx={tdSx}>
                <CellTextField
                  value={connection.username}
                  onChange={(v) => updateConnection("username", v)}
                  disabled={!isEditMode && !isNewConfig}
                  placeholder="Username"
                />
              </TableCell>
              <TableCell sx={tdSx}>
                <TextField
                  size="small"
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  value={connection.password}
                  onChange={(e) => {
                    updateConnection("password", e.target.value);
                  }}
                  disabled={!isEditMode && !isNewConfig}
                  placeholder="Password"
                  inputProps={ipSx}
                  sx={!isEditMode ? disabledInputSx : enabledInputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword((p) => !p)}
                          disabled={!isEditMode && !isNewConfig}
                          edge="end"
                          sx={{
                            color:
                              isEditMode || isNewConfig ? APP_BLUE : "#ccc",
                          }}
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ fontSize: "1rem" }} />
                          ) : (
                            <Visibility sx={{ fontSize: "1rem" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Doc rows table */}
      <TableContainer
        sx={{ overflowX: "auto", borderTop: "2px solid #b0c4de" }}
      >
        <Table size="small" sx={{ tableLayout: "fixed", minWidth: 500 }}>
          <colgroup>
            <col style={{ width: "20%" }} />
            <col style={{ width: "40%" }} />
            {/* Bug 2: Incoming Directory is now read-only (populated via gear icon config),
                same as File Name Format. Width unchanged. */}
            <col style={{ width: "35%" }} />
            <col style={{ width: "5%" }} />
          </colgroup>
          <TableHead>
            <TableRow>
              {[
                "DOC Type",
                "File Name Format",
                "Incoming Directory",
                "",
              ].map((h, i) => (
                <TableCell key={i} sx={subThSx}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {docRows.map((row) => (
              <TableRow
                key={row.id}
                sx={{ "&:hover": { backgroundColor: "#fafafa" } }}
              >
                <TableCell sx={tdSx}>
                  <DocTypeSelect
                    value={row.docType}
                    onChange={(v) => handleDocTypeSelect(row.id, v)}
                    disabled={!isEditMode && !isNewConfig}
                    documentTypeOptions={documentTypeOptions}
                    selectedDocTypes={selectedDocTypes}
                  />
                </TableCell>
                <TableCell sx={{ ...tdSx, maxWidth: "100%", overflow: "hidden" }} title={row.fileNameFormat || ""}>
                  <CellTextField
                    value={row.fileNameFormat}
                    onChange={(v) => updateDocRow(row.id, "fileNameFormat", v)}
                    disabled
                    placeholder="Configure via gear icon"
                  />
                </TableCell>
                {/* Bug 2 fix: always disabled — populated via gear icon config only */}
                <TableCell sx={{ ...tdSx, maxWidth: "100%", overflow: "hidden" }} title={row.incomingDirectory || ""}>
                  <CellTextField
                    value={row.incomingDirectory}
                    onChange={(v) => updateDocRow(row.id, "incomingDirectory", v)}
                    disabled
                    placeholder="Configure via gear icon"
                  />
                </TableCell>
                <TableCell
                  sx={{ ...tdSx, textAlign: "center", padding: "4px 2px" }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(row.id)}
                  >
                    <DeleteIcon
                      sx={{
                        fontSize: "1rem",
                        color: "#d32f2f",
                      }}
                    />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // ── Bug 9 fix: Save/Cancel visibility ────────────────────────────────────
  // For a fresh/new config: only show Save/Cancel once the user has actually
  // made a change (isDirty=true) AND has selected a delivery method.
  // For an existing config being edited: show when isDirty=true.
  // Previously `isEditMode` was always true for new configs which caused the
  // disabled Save/Cancel to appear immediately on page load.
  const showSaveCancel = isDirty;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3}>
          <InputLabel
            sx={{
              fontSize: "0.85rem",
              color: "#333",
              mb: 0.5,
              fontWeight: 500,
            }}
          >
            Doc Delivery Method
          </InputLabel>
          <Select
            fullWidth
            size="small"
            value={selectedMethodCode}
            onChange={(e) => handleMethodChange(e.target.value)}
            disabled={!isEditMode && !isNewConfig}
            displayEmpty
            sx={
              isEditMode || isNewConfig
                ? { fontSize: "0.85rem", ...enabledSelectSx }
                : { fontSize: "0.85rem", ...disabledSelectSx }
            }
          >
            <MenuItem value="" disabled sx={{ fontSize: "0.85rem" }}>
              <em>Select</em>
            </MenuItem>
            {DELIVERY_METHODS.map((opt) => (
              <MenuItem
                key={opt.code}
                value={opt.code}
                sx={{ fontSize: "0.85rem" }}
              >
                {opt.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid
          item
          xs={12}
          md={1}
          lg={1}
          sx={{ display: "flex", alignItems: "end", pb: 0.3 }}
        >
          <IconButton
            onClick={openFileNameModal}
            disabled={(!isEditMode && !isNewConfig) || !selectedMethodCode}
            sx={{
              border: "1px solid #b0c4de",
              borderRadius: 1,
              color: APP_BLUE,
            }}
            title="Configure File Name Format"
          >
            <SettingsIcon sx={{ fontSize: "1.1rem" }} />
          </IconButton>
        </Grid>

        {isLoading ? (
          <Grid item xs={12}>
            <Typography sx={{ fontSize: "0.85rem", color: "#888", mt: 1 }}>
              Loading...
            </Typography>
          </Grid>
        ) : selectedMethodCode ? (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.82rem",
                fontWeight: 700,
                mb: 0.5,
                color: APP_BLUE,
                letterSpacing: "0.02em",
              }}
            >
              PRE-INVOICE REQUIREMENTS (POP)
            </Typography>
            {isEmail && renderEmailTable()}
            {isSftpFtp && renderSftpTable()}
            <Button
              size="small"
              onClick={addDocRow}
              endIcon={
                <AddCircleOutlineIcon
                  style={{ width: "16px", height: "16px" }}
                />
              }
              disabled={(!isEditMode && !isNewConfig) || allDocTypesUsed}
              sx={{
                mt: 1,
                fontSize: "0.78rem",
                textTransform: "none",
                textDecoration: "underline",
                color:
                  (isEditMode || isNewConfig) && !allDocTypesUsed
                    ? APP_BLUE
                    : "#aaa",
              }}
            >
              + ADD ROW
            </Button>
          </Grid>
        ) : null}

        {/* Bug 9 fix: only render Save/Cancel when isDirty=true */}
        {showSaveCancel && (
          <Grid
            item
            xs={12}
            sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}
          >
            <Button
              variant="contained"
              size="small"
              disabled={isSaving || !selectedMethodCode}
              onClick={handleSave}
              startIcon={
                <SaveIcon style={{ width: "14px", height: "14px" }} />
              }
              sx={{
                fontSize: "0.85rem",
                backgroundColor: APP_BLUE,
                "&:hover": { backgroundColor: "rgb(8,50,105)" },
                "&.Mui-disabled": { backgroundColor: "#ccc", color: "#fff" },
              }}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCancelEdit}
              startIcon={
                <CancelIcon style={{ width: "14px", height: "14px" }} />
              }
              sx={{
                fontSize: "0.85rem",
                color: "#555",
                borderColor: "#aaa",
                "&:hover": { borderColor: "#888" },
              }}
            >
              Cancel
            </Button>
          </Grid>
        )}
      </Grid>

      <Snackbar
        open={snackOpen}
        style={{ marginTop: "5%" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnackOpen(false)}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={snackContent.severity}
          sx={{ width: "100%" }}
        >
          {snackContent.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Row"
        message="Are you sure you want to delete this row? This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      <ConfirmDialog
        open={methodChangeDialogOpen}
        title="Change Delivery Method"
        message="Changing the delivery method will remove your existing delivery method configuration. Do you want to continue?"
        confirmLabel="Change"
        confirmColor="warning"
        onConfirm={() => applyMethodChange(pendingMethodCode)}
        onCancel={() => {
          setMethodChangeDialogOpen(false);
          setPendingMethodCode(null);
        }}
      />

      <ConfirmDialog
        open={deleteAllDialogOpen}
        title="Delete All Delivery Config"
        message="This will delete all delivery configuration for this Bill To. This action cannot be undone. Do you want to continue?"
        confirmLabel={isDeleting ? "Deleting..." : "Delete All"}
        onConfirm={handleDeleteAll}
        onCancel={() => setDeleteAllDialogOpen(false)}
      />

      <FileNameFormatModal
        open={fileNameModalOpen}
        onClose={() => setFileNameModalOpen(false)}
        methodCode={selectedMethodCode}
        billToCode={billToCode}
        orderFieldTokens={orderFieldTokens}
        documentTypeOptions={documentTypeOptions}
        dispatch={dispatch}
      />
    </>
  );
}
